import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CasingCreditRepair,
  CasingCreditRepairFilters,
  PaginatedCasingCreditRepairResponse,
  Pagination
} from '../../models/response.model';
import { map } from 'rxjs';
import { FeatureflagsService } from 'src/app/shared/services/featureflags.service';
import { FEATURE_FLAGS } from 'src/app/shared/constants/constants';

@Component({
  selector: 'app-casing-repair',
  templateUrl: './casing-credit-repair.component.html',
  styleUrl: './casing-credit-repair.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingCreditRepairComponent implements OnInit {
  @ViewChild('deleteContent') deleteContent: TemplateRef<any>;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('deleteConfirmation', { static: true }) deleteModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  public route = inject(ActivatedRoute);

  pagination: Pagination;
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  tabRouteLink: string = '/productData';
  isInitialPatch = true;
  casingRepairsList: CasingCreditRepair[] = [];
  codes: string[];
  descriptions: string[];
  hasFetchedDropDowns = false;
  private itemToDelete: string;
  displayedRowsForm: FormGroup = this.fb.group({
    rows: this.fb.array([])
  });

  productSaveDisabled = true;

  tableConfig: TableConfig<CasingCreditRepair> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    onRowClick: (elm: CasingCreditRepair) => {
      this.router.navigateByUrl(`/home/productData/casing-CreditRepair/casingcreditrepairdetails/${elm.casingCreditRepairPk}`, {
        state: { code: elm.code, description: elm.description }
      });
    },
    columns: {}
  };

  state = TableConfig.getState(this.tableConfig, this.route);

  formGroup = this.fb.group({
    codes: [this.state.extraFilters?.codes ?? []],
    descriptions: [this.state.extraFilters?.descriptions ?? []]
  });

  fetcher: PaginatorFetcher<CasingCreditRepair> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;

    const casingRepairFilters: CasingCreditRepairFilters = this.formGroup.value as CasingCreditRepairFilters;
    return this.productDataService.getCasingCreditRepairs(casingRepairFilters, filters.page, filters.limit).pipe(
      map((casingRepairResponse: PaginatedCasingCreditRepairResponse) => {
        this.casingRepairsList = casingRepairResponse.data.casingCreditRepairs || [];
        this.pagination = casingRepairResponse.data.pagination;
        this.updateDisplayedRowsForm(this.casingRepairsList);
        return { items: this.casingRepairsList, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {}

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.productDataService.getCasingCreditRepairFilters().subscribe({
      next: (response) => {
        this.codes = response.data.repairCodes || [];
        this.descriptions = response.data.repairDescriptions || [];
        this.cdr.markForCheck();
      }
    });

    if (this.tableRef) {
      this.tableRef.patchExtraFilters(this.formGroup.value, false);
    }
    this.setupFormListeners();
    this.loadInitialData();
    this.configureColumnsOnLanguageChange();
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        code: {
          cell: (elm: CasingCreditRepair) => elm.code,
          header: this.translationService.translate('codeText')
        },
        description: {
          cell: (elm: CasingCreditRepair) => elm.description,
          header: this.translationService.translate('descriptionText')
        },
        action: {
          header: this.translationService.translate('actionText'),
          cell: 'action'
        }
      };
      this.cdr.markForCheck();
    });
    if (this.tableRef) {
      this.tableRef.patchExtraFilters(this.formGroup.value, false);
    }
    this.cdr.markForCheck();
  }

  private setupFormListeners(): void {
    this.formGroup.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((values) => {
      if (this.isInitialPatch) {
        this.isInitialPatch = false;
        return;
      }
      this.tableRef.patchExtraFilters(values, true);
    });
  }

  private loadInitialData(): void {
    // Load any initial data if needed
    this.hasFetchedDropDowns = true;
  }

  private configureColumnsOnLanguageChange(): void {
    this.tableConfig.onColumnsChange = () => {
      // Configure column visibility if needed
    };
  }

  private createRowForm(row: CasingCreditRepair) {
    return this.fb.group({
      code: [row.code],
      description: [row.description],
      repairPk: [row.casingCreditRepairPk]
    });
  }

  public updateDisplayedRowsForm(rows: CasingCreditRepair[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  getControl(item: CasingCreditRepair, controlName: keyof CasingCreditRepair): FormControl {
    const index = this.casingRepairsList.findIndex((row) => row.casingCreditRepairPk === item.casingCreditRepairPk);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  resetFilters(): void {
    this.formGroup.reset();
  }

  getModifiedRows(): any[] {
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.casingRepairsList.length; i++) {
      const original = { ...this.casingRepairsList[i] };
      const current = { ...formArray.at(i).value };
      const isModified = Object.keys(current).some((key) => current[key] !== original[key]);
      if (isModified) {
        modifiedRows.push({ ...formArray.at(i).value, repairPk: this.casingRepairsList[i].casingCreditRepairPk });
      }
    }
    return modifiedRows;
  }

  exportTable(): void {}

  createCasingRepair(): void {
    this.router.navigateByUrl('/home/productData/casing-CreditRepair/create');
  }

  showDeleteModal(item) {
    this.deleteModal.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText'),
      width: 550,
      content: this.deleteContent,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
    this.itemToDelete = item.casingCreditRepairPk;
  }

  onDeleteModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'deleteConfirmation' && result.result) {
        this.deleteRow(this.itemToDelete);
      } else {
        this.itemToDelete = null;
      }
    }
  }

  private deleteRow(itemToDelete: string) {
    this.productDataService.deleteCasingCreditRepair(itemToDelete).subscribe({
      next: () => {
        this.alertService.showAlert({
          content: this.translationService.translate('casingCreditRepairDeletedSuccessfully'),
          type: AlertType.success
        } as Alert);
        // Refresh data after successful deletion
        this.tableRef.resetFilters();
      },
      error: (error) => {
        this.alertService.showAlert({
          content: error?.error?.message || this.translationService.translate('tryAgainText'),
          type: AlertType.critical
        } as Alert);
      }
    });
  }

  protected isDeletable(item: CasingCreditRepair): boolean {
    const roles: string = localStorage.getItem('roles') || '';
    return !item.deletable && roles.includes('PRODUCT_SETUP_EDIT');
  }
}
